import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { findFormulaRoute } from '../../lib/formulaSolver';
import { fetchQuestionsByFormulaId } from '../../lib/api';
import rawQuestions from '../../db/initialQuestions.json';
import { Question, FormulaRouteResult, FormulaRouteChain } from '../../types';

export function useFormulaSolver() {
  const [selectedKnowns, setSelectedKnowns] = useState<string[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [activeChainIndex, setActiveChainIndex] = useState<number>(0);

  // Pre-index static questions from seed once synchronously
  const staticQuestionsMap = useMemo(() => {
    const map: Record<string, Question[]> = {};
    for (const q of rawQuestions as Question[]) {
      if (q.formulaId) {
        if (!map[q.formulaId]) map[q.formulaId] = [];
        map[q.formulaId].push(q);
      }
    }
    return map;
  }, []);

  const [questionsByFormulaId, setQuestionsByFormulaId] =
    useState<Record<string, Question[]>>(staticQuestionsMap);
  const fetchedFormulaIdsRef = useRef<Set<string>>(new Set());

  // Reset active chain index if target or knowns change
  useEffect(() => {
    setActiveChainIndex(0);
  }, [selectedKnowns, selectedTarget]);

  // Compute deterministic deduction result
  const finderResult = useMemo<FormulaRouteResult | null>(() => {
    if (!selectedTarget || selectedKnowns.length === 0) return null;
    return findFormulaRoute(selectedKnowns, selectedTarget);
  }, [selectedKnowns, selectedTarget]);

  // Active chain to display
  const activeChain = useMemo<FormulaRouteChain | null>(() => {
    if (!finderResult || !finderResult.found || finderResult.chains.length === 0) return null;
    return finderResult.chains[activeChainIndex] || finderResult.chains[0];
  }, [finderResult, activeChainIndex]);

  // Query API asynchronously for any newly added questions
  useEffect(() => {
    if (!activeChain) return;

    for (const step of activeChain.steps) {
      const fId = step.equation.id;
      if (fetchedFormulaIdsRef.current.has(fId)) continue;
      fetchedFormulaIdsRef.current.add(fId);

      fetchQuestionsByFormulaId(fId)
        .then((backendQuestions) => {
          if (backendQuestions && backendQuestions.length > 0) {
            setQuestionsByFormulaId((prev) => ({
              ...prev,
              [fId]: backendQuestions,
            }));
          }
        })
        .catch(() => {
          // ignore network failures, fallback to static cache
        });
    }
  }, [activeChain]);

  const toggleKnown = useCallback((id: string) => {
    setSelectedKnowns((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const removeKnown = useCallback((id: string) => {
    setSelectedKnowns((prev) => prev.filter((x) => x !== id));
  }, []);

  const addKnown = useCallback((id: string) => {
    setSelectedKnowns((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setSelectedTarget((prev) => (prev === id ? '' : prev));
  }, []);

  const setTarget = useCallback((id: string) => {
    setSelectedTarget((prev) => (prev === id ? '' : id));
    setSelectedKnowns((prev) => prev.filter((x) => x !== id));
  }, []);

  const clearAll = useCallback(() => {
    setSelectedKnowns([]);
    setSelectedTarget('');
    setActiveChainIndex(0);
  }, []);

  return {
    selectedKnowns,
    selectedTarget,
    activeChainIndex,
    setActiveChainIndex,
    finderResult,
    activeChain,
    questionsByFormulaId,
    toggleKnown,
    removeKnown,
    addKnown,
    setTarget,
    clearAll,
  };
}
